import React, { ComponentProps, useMemo } from 'react';

import { useCompanies, useUsers } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import Tooltip from '@adsk/alloy-react-tooltip/es/Tooltip';

import { MentionData } from '../../consts';
import { useTranslation } from '../../i18n';
import ShowMore from '../ShowMore';

type MentionCompanyTooltipProps = Omit<
  ComponentProps<typeof Tooltip>,
  'content'
> & {
  mention: MentionData;
};

export const MentionCompanyTooltip = ({
  mention,
  children,
  ...props
}: MentionCompanyTooltipProps) => {
  const { t } = useTranslation();
  const companies = useCompanies();
  const users = useUsers();
  const currentCompany = useMemo(
    () =>
      companies.data.find((company) => company.memberGroupId === mention.id),
    [companies.data, mention.id],
  );

  return (
    <Tooltip
      {...props}
      content={
        <Flex
          data-testid={`MentionCompanyTooltip_Content_${mention?.id}`}
          alignItems="center"
          style={{ wordBreak: 'break-all' }}
        >
          <span>
            <Box typography="labelMedium" color="charcoal900">
              {currentCompany?.name || mention?.name || t('FORMER_COMPANY')}
            </Box>
            <Box typography="labelMedium" color="charcoal700">
              <ShowMore
                list={users.data
                  ?.filter((user) => user.companyId === currentCompany?.id)
                  .map((user) => user.name)}
              />
            </Box>
          </span>
        </Flex>
      }
      triggerTarget={Tooltip.TRIGGER_TARGETS.TOOLTIP}
      variant={Tooltip.VARIANTS.LIGHT}
    >
      <span data-testid={`MentionCompanyTooltip_Trigger_${mention?.id}`}>
        @
        {children ||
          currentCompany?.name ||
          mention?.name ||
          t('FORMER_COMPANY')}
      </span>
    </Tooltip>
  );
};
